import { useId, useLayoutEffect, useRef, useState, type Ref } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Signpost } from "lucide-react";
import { Button } from "@/components/design/Button";
import type { FollowUpView, LocalizedText, OptionView, StoryLanguage } from "@/lib/games/roleplay/contract";
import { fill, followUpsOf, unfill } from "@/lib/games/roleplay/options";
import { codePoints } from "@/lib/games/roleplay/story";
import { CourseOptions } from "./CourseOptions";
import { LOCKED_BUTTON } from "./locked-button";

// The player acts through their own words, always (input rule, 79): nothing is sent until "Send" is pressed. Unlike
// Studio's notebook, no key in the field sends: Enter starts a new line, as Shift+Enter does, the plain behaviour of a
// textarea, since the game asks for longer descriptions and hardly anyone noticed that Enter sent (spark#1236). A
// keyboard player tabs from the field to "Send", the next stop, and presses Enter or Space there. The placeholder asks
// for what the guide asks for (102): how the player feels, what they think and what they do, and why. The field itself
// is never disabled (23.1): while the player waits for the turn they can type, only not send it. While the story runs
// the player writes on, and `canSend` alone holds back the button. Only while the guide shows it (`locked`) is it
// switched off, so it takes no click and no caret blinks in it: during the tour nothing can be typed into it, and
// "Send" stays off with it. The courses of action are switched off with the field unless `optionsLocked` says
// otherwise: the guide's move step lets the player try them as in the game (spark#1236), so a follow-up clicked
// there puts its template into the switched off field and counts as the click (`onPicked`), only as a try: the page
// takes both back once the guide is over. The caret cannot go into the switched off field then, so the focus stays on
// the follow-up, and a polite live region says that the start of the move is now in the field. The panel of options
// the try left open closes as the field is switched on, so the game opens on its options.
//
// A scene's poles (4, 23) stand above the field as a short labelled list of plain text (23.3: "Mögliche
// Handlungsmöglichkeiten"): orientation for where the story can go, never a button, a toggle or anything else that
// invites a click. That rule holds for poles only. A scene that has clickable courses of action (`options`, play
// contract §3.5; agent-service#129, spark#1075) shows them in the same place instead (CourseOptions), and they only
// fill the field: a follow-up's template goes to the start of the field with one space after it, whatever the player
// had typed stays behind it, and the caret lands after the space (options.ts holds the rule). The move is still the
// player's typed words, and nothing is sent until "Send". A field holding nothing but a template is an empty move
// (game-store's `canSend`), so "Send" is switched off then, as for an empty field.
//
// Where the scene offers courses of action, a turn goes out only once the player has clicked one of the follow-ups for
// it, a way to carry the action out (spark#1236; the store keeps that click, `onPicked`). Opening an option is not that
// click, and „Zurück“ takes it back (`onUnpicked`) together with the words it inserted, so free text alone never goes
// out after it; a follow-up clicked after that counts anew. Until then (`needsPick`) "Send" sends nothing and a
// short line under the field says why, in a polite live region, which hears every refusal anew: each one mounts the
// line again (`refusals` as its key). "Send" then only reads as switched off (`aria-disabled`, with the look of the
// page's other such buttons and no hover) and keeps its place in the tab order, so the focus stays where it was; it
// is described by that line, hidden until a refusal shows it, so tabbing to it says why it is locked. The field itself
// stays the player's, as before: after the click they change and add to the inserted words, since the field carries
// how and why they act and how they feel, which the assessment reads. A scene without courses of action needs no click.
//
// It wears Save Studio's composer, so the game and Studio are one UI (StudioNotebookStream): a box with Studio's
// field, and under it the button that sends, with no key cap, since no key in the field sends; above the field its
// visible label (22). `pinned` sets it in Studio's band at the bottom of the visible area (22), over the story box's
// own fill so the story scrolls away behind it; the page passes it once the guide is over, and `bandRef` to see what the band covers.
// The list of courses of action stays in the story's flow above the band, so the band stays small. The two are
// siblings, so the band can stick along the whole story box. The field wears no AI signal band under it (spark#1075):
// nothing shows between the field and the counter row but the form's own background. `highlight` gives the field's box the guide's outline while the guide hands over to the game (index.css,
// .grow-input-handover).

// Studio's composer field (StudioNotebookStream). `block`, since a textarea is inline-block and would leave a strip of
// its line's descender gap inside the box around it, under the field (spark#1075).
const FIELD =
  "block smycles-field-value field-sizing-content max-h-56 min-h-22 w-full resize-none rounded-md bg-page p-3 ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-ink-ui";

// Studio's band, inside the story box: out to the box's edges (its p-5), so the story cannot show beside or below it.
const PINNED = "sticky bottom-0 z-[var(--z-raised)] -mx-5 -mb-5 rounded-b-md bg-surface px-5 pb-5";

export function TurnInput({
  poles,
  options = [],
  scene = 0,
  needsPick = false,
  language,
  text,
  maxLength,
  canSend,
  pinned = false,
  bandRef,
  highlight = false,
  locked = false,
  optionsLocked = locked,
  onText,
  onSend,
  onPicked,
  onUnpicked,
}: {
  poles: readonly { id: string; text: LocalizedText }[];
  options?: readonly OptionView[];
  /** The scene the options belong to: a new scene opens on its options, not on a follow-up panel left open. */
  scene?: number;
  /** Whether a click on one of the offered follow-ups is what still holds the turn back (spark#1236). */
  needsPick?: boolean;
  language: StoryLanguage;
  text: string;
  maxLength: number;
  canSend: boolean;
  pinned?: boolean;
  bandRef?: Ref<HTMLDivElement>;
  highlight?: boolean;
  locked?: boolean;
  /** Whether the courses of action are switched off; with the field (`locked`) unless the page says otherwise. */
  optionsLocked?: boolean;
  onText: (text: string) => void;
  onSend: () => void;
  onPicked?: () => void;
  onUnpicked?: () => void;
}) {
  const intl = useIntl();
  const polesLabel = useId();
  const fieldId = useId();
  const counterId = useId();
  const tooLongId = useId();
  const pickId = useId();
  // How often "Send" was refused for the missing click; the line goes once the click is made or the turn is
  // sent. The count keys the line, so every refusal puts it into the live region anew.
  const [refusals, setRefusals] = useState(0);
  // A refusal no longer holds once the click is no longer missing (made, or the story busy again).
  if (refusals > 0 && !needsPick) setRefusals(0);
  const pickHint = refusals > 0 && needsPick;
  // How often a follow-up put its template into the switched off field; it keys the line that says so, so every such
  // click is heard anew, and the line goes with the lock.
  const [inserted, setInserted] = useState(0);
  if (inserted > 0 && !locked) setInserted(0);
  const length = codePoints(text);
  const tooLong = length > maxLength;
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  // Where the caret goes once the field shows the text a follow-up wrote (the store renders it, not this click).
  const caret = useRef<number | null>(null);

  const placeCaret = () => {
    const field = fieldRef.current;
    if (caret.current === null || !field) return;
    const at = Math.min(caret.current, field.value.length);
    caret.current = null;
    field.focus();
    field.setSelectionRange(at, at);
  };
  useLayoutEffect(placeCaret);

  const send = () => {
    if (canSend) {
      setRefusals(0);
      onSend();
    } else if (needsPick) setRefusals((count) => count + 1);
  };

  const onPick = (followUp: FollowUpView) => {
    onPicked?.();
    if (locked) setInserted((count) => count + 1);
    const filled = fill(text, followUp, followUpsOf(options), language);
    caret.current = filled.caret;
    if (filled.text === text) placeCaret();
    else onText(filled.text);
  };

  const onBack = (option: OptionView) => {
    onUnpicked?.();
    // the pick is gone, so the line saying its words are in the field would no longer be true (spark#1236)
    setInserted(0);
    const rest = unfill(text, option, language);
    if (rest !== text) onText(rest);
  };

  const above = poles.length > 0 || options.length > 0;

  return (
    <>
      {poles.length > 0 ? (
        <div className="mt-6">
          <p id={polesLabel} className="smycles-field-label">
            <FormattedMessage id="grow.roleplay.input.poles" defaultMessage="Possible courses of action" />
          </p>
          <ul aria-labelledby={polesLabel} className="mt-1.5 space-y-1">
            {poles.map((pole) => (
              <li key={pole.id} className="flex items-start gap-2 smycles-field-meta text-ink">
                <Signpost aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-ink-muted" strokeWidth={1.6} />
                <span className="min-w-0">{pole.text[language]}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <CourseOptions
        key={`${scene}${locked ? ":locked" : ""}`}
        options={options}
        language={language}
        disabled={optionsLocked}
        onPick={onPick}
        onBack={onBack}
        onFocusLost={() => fieldRef.current?.focus({ preventScroll: true })}
      />
      <div ref={bandRef} className={`${above ? "mt-1" : "mt-4"} pt-2${pinned ? ` ${PINNED}` : ""}`}>
        <form
          className="bg-surface box-flat p-3"
          onSubmit={(event) => {
            event.preventDefault();
            send();
          }}
        >
          <label htmlFor={fieldId} className="smycles-field-label">
            <FormattedMessage id="grow.roleplay.input.label" defaultMessage="What do you do?" />
          </label>
          <div className={`mt-1.5 rounded-md${highlight ? " grow-input-handover" : ""}`}>
            <textarea
              ref={fieldRef}
              id={fieldId}
              value={text}
              rows={3}
              disabled={locked}
              onChange={(event) => onText(event.target.value)}
              aria-invalid={tooLong}
              // The counter, and once the move is too long or still needs its click the hint, say why "Send" does nothing.
              aria-describedby={[counterId, tooLong ? tooLongId : null, pickHint ? pickId : null].filter(Boolean).join(" ")}
              placeholder={intl.formatMessage({
                id: "grow.roleplay.input.placeholder",
                defaultMessage: "Describe how you feel, what you think and what you do, and why.",
              })}
              className={FIELD}
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span id={counterId} className={`smycles-field-meta tabular-nums ${tooLong ? "text-ink" : "text-ink-muted"}`}>
              <FormattedMessage
                id="grow.roleplay.input.counter"
                defaultMessage="{count} / {max} characters"
                values={{ count: length, max: maxLength }}
              />
            </span>
            <Button
              type="submit"
              size="compact"
              disabled={!canSend && !needsPick}
              aria-disabled={needsPick || undefined}
              aria-describedby={needsPick ? pickId : undefined}
              className={LOCKED_BUTTON}
            >
              <FormattedMessage id="grow.roleplay.input.send" defaultMessage="Send" />
            </Button>
          </div>
          {tooLong ? (
            <p id={tooLongId} className="mt-2 smycles-field-meta text-ink">
              <FormattedMessage
                id="grow.roleplay.input.tooLong"
                defaultMessage="At most {max} characters."
                values={{ max: maxLength }}
              />
            </p>
          ) : null}
          {/* Always there, so a screen reader hears the line when it comes, the focus staying on "Send". While the click
              is missing the line is there for "Send" to be described by, and hidden until a refusal shows it. */}
          <div aria-live="polite">
            {needsPick ? (
              <p key={refusals} id={pickId} hidden={!pickHint} className="mt-2 smycles-field-meta text-ink">
                <FormattedMessage
                  id="grow.roleplay.input.pickFirst"
                  defaultMessage="Choose how you carry out one of the courses of action. In the field you can then describe how and why you do it and how you feel about it."
                />
              </p>
            ) : null}
          </div>
          {/* The focus stays on the follow-up while the field is switched off, so this tells a screen reader what the
              click did there. */}
          <div aria-live="polite" className="sr-only">
            {inserted > 0 ? (
              <p key={inserted}>
                <FormattedMessage
                  id="grow.roleplay.input.templateInserted"
                  defaultMessage="The start of your move is now in the field."
                />
              </p>
            ) : null}
          </div>
        </form>
      </div>
    </>
  );
}
